import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ApiError,
  cancelPayment,
  getPaymentMethods,
  getPaymentStatus,
  startPayment,
} from '../api/client'
import type {
  Fulfilment,
  Nachweis,
  Order,
  PaymentMethod,
  PaymentStart,
  PaymentState,
} from '../api/types'
import { useT } from '../i18n'
import { usePriceFormat } from '../shopConfig'
import { Ladehinweis } from './States'

/**
 * Bezahlen.
 *
 * Der Laden bestimmt, welche Arten es gibt; diese Seite unterscheidet nur noch
 * drei Formen, und das ist der ganze Kniff. Sie unterscheiden sich nicht im
 * Aussehen, sondern darin, was der Kunde als Nächstes tun soll:
 *
 *   qr        Code anzeigen, danebenstehen, warten
 *   redirect  zum Anbieter schicken, auf die Rückkehr warten
 *   manual    nichts weiter — der Laden wartet auf Geld
 *
 * Bei `manual` steht deshalb ausdrücklich **kein** Knopf: Es gibt nichts zu
 * drücken. Ein Knopf, der nur eine Seite neu lädt, wäre ein Versprechen, das
 * niemand einlöst — der Kunde überweist oder zahlt im Laden.
 *
 * Ob bezahlt wurde, sagt allein der Server. Diese Seite fragt, sie behauptet
 * nie. Und sie gibt nicht nach einer Weile von selbst auf: Eine Zahlung, die
 * für gescheitert erklärt wird, während das Geld unterwegs ist, ist genau der
 * Streit, den niemand führen will.
 */

/** Abstand zwischen zwei Nachfragen. */
const NACHFRAGE_MS = 3000

export function PaymentPanel({
  nummer,
  nachweis,
  zustellart,
  vorauswahl,
  onBezahlt,
}: {
  nummer: string
  /**
   * Womit sich der Kunde ausweist — Schlüssel oder E-Mail.
   *
   * Vorher stand hier nur die E-Mail, und der Zahlungsbereich hing daran:
   * `status === 'awaiting_payment' && email.trim()`. Wer von der Bezahlseite
   * eines Anbieters zurückkam, brachte keine mit — und sah deshalb gar keine
   * Möglichkeit zu zahlen oder den Stand zu sehen. Das war der eigentliche
   * Grund, aus dem der Bezahlweg „nicht ging".
   */
  nachweis: Nachweis
  /** Bestimmt, welche Arten zur Wahl stehen — Barzahlung gibt es nur bei Abholung. */
  zustellart: Fulfilment
  /** Was an der Kasse gewählt wurde. Dann fängt die Zahlung von allein an. */
  vorauswahl?: string
  onBezahlt: (bestellung: Order) => void
}) {
  const t = useT()
  const preis = usePriceFormat()

  const [arten, setArten] = useState<PaymentMethod[] | null>(null)
  const [gewaehlt, setGewaehlt] = useState(vorauswahl ?? '')
  const [start, setStart] = useState<PaymentStart | null>(null)
  const [zustand, setZustand] = useState<PaymentState>('none')
  const [nochEinmal, setNochEinmal] = useState(true)
  const [startet, setStartet] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [nichtVerfuegbar, setNichtVerfuegbar] = useState(false)

  // Damit die Nachfrage-Schleife nicht gegen einen alten Zustand arbeitet,
  // wenn die Seite währenddessen verlassen wird.
  const lebt = useRef(true)
  useEffect(() => {
    lebt.current = true
    return () => {
      lebt.current = false
    }
  }, [])

  /** Die Sperre gegen den zweiten Klick — sie steht sofort, ein Zustand erst nach dem Rendern. */
  const laeuft = useRef(false)

  const anfordern = useCallback(
    async (methode: string) => {
      if (!methode || laeuft.current) return
      laeuft.current = true
      setStartet(true)
      setFehler(null)

      try {
        const neu = await startPayment(nummer, nachweis, methode)
        if (!lebt.current) return
        setZustand(neu.state)
        if (neu.state === 'paid') {
          // Zwischen Bestellen und Bezahlseite schon bezahlt — etwa, weil der
          // Kunde die Seite zweimal offen hatte.
          const stand = await getPaymentStatus(nummer, nachweis)
          if (lebt.current && stand.order) onBezahlt(stand.order)
          return
        }
        setStart(neu)
      } catch (ausnahme) {
        if (!lebt.current) return
        // Der Satz des Servers unverändert; nur wenn gar keiner kommt, spricht
        // das Ladenfenster selbst.
        const meldung =
          ausnahme instanceof ApiError ? ausnahme.message : t('bezahlen.nichtAngefordert')
        // 501 heißt: Dieser Laden nimmt derzeit keine Zahlungen an. Das ist
        // kein Fehler, sondern eine Auskunft — und dann hat dieser Abschnitt
        // hier nichts zu suchen.
        if (ausnahme instanceof ApiError && ausnahme.status === 501) setNichtVerfuegbar(true)
        else setFehler(meldung)
      } finally {
        laeuft.current = false
        if (lebt.current) setStartet(false)
      }
    },
    [nummer, nachweis, onBezahlt, t],
  )

  // Die Arten holen, die für diese Bestellung überhaupt in Frage kommen.
  useEffect(() => {
    const steuerung = new AbortController()
    getPaymentMethods(zustellart, steuerung.signal)
      .then((liste) => {
        if (!lebt.current) return
        setArten(liste)
        // Eine einzige Art ist keine Wahl: Dann steht sie nicht als
        // Radioknopfreihe da, sondern wird genommen.
        setGewaehlt((vorher) => vorher || (liste.length === 1 ? liste[0]!.key : ''))
      })
      .catch(() => {
        if (lebt.current) setArten([])
      })
    return () => steuerung.abort()
  }, [zustellart])

  // Einmal fragen, wie es um diese Bestellung steht, bevor irgendetwas
  // angefordert wird.
  //
  // Das ist der Weg zurück von einer Bezahlseite: Der Kunde kommt über eine
  // fremde Seite wieder herein, und ob dabei Geld geflossen ist, weiß nur der
  // Server. Läuft noch eine Anforderung, wird sie mit derselben Art wieder
  // aufgenommen — der Server gibt dann dieselbe zurück und legt keine zweite an.
  const ersteFrage = useRef(false)
  useEffect(() => {
    if (ersteFrage.current) return
    ersteFrage.current = true

    void getPaymentStatus(nummer, nachweis)
      .then((stand) => {
        if (!lebt.current) return
        setZustand(stand.payment.state)
        setNochEinmal(stand.payment.canRetry)
        if (stand.payment.paid) {
          if (stand.order) onBezahlt(stand.order)
          return
        }
        if (stand.payment.state === 'pending' && stand.payment.method) {
          setGewaehlt(stand.payment.method)
          void anfordern(stand.payment.method)
          return
        }
        if (vorauswahl) void anfordern(vorauswahl)
      })
      .catch(() => {
        // Eine misslungene erste Frage ist kein Grund, nichts anzubieten: Der
        // Kunde kann die Zahlung trotzdem anfordern.
        if (lebt.current && vorauswahl) void anfordern(vorauswahl)
      })
  }, [nummer, nachweis, vorauswahl, anfordern, onBezahlt])

  // Nachfragen, solange eine Anforderung läuft.
  //
  // Nicht bei `manual`: Dort gibt es niemanden zu fragen. Der Laden bucht die
  // Zahlung, wenn das Geld auf dem Konto liegt — das dauert Tage, und so lange
  // sitzt niemand vor dieser Seite.
  useEffect(() => {
    if (!start || start.kind === 'manual' || zustand === 'paid') return

    let gestoppt = false
    const uhr = setInterval(async () => {
      try {
        const stand = await getPaymentStatus(nummer, nachweis)
        if (gestoppt || !lebt.current) return

        setZustand(stand.payment.state)
        setNochEinmal(stand.payment.canRetry)

        if (stand.payment.paid) {
          if (stand.order) onBezahlt(stand.order)
          return
        }
        // Abgelaufen oder gescheitert: Der Kunde bekommt einen Weg weiter,
        // statt vor einem Code zu sitzen, der nichts mehr tut.
        if (stand.payment.state === 'failed' || stand.payment.state === 'expired') {
          setStart(null)
        }
      } catch {
        // Eine einzelne fehlgeschlagene Nachfrage sagt nichts über die
        // Zahlung — das Netz wackelt öfter als eine Zahlung scheitert. Beim
        // nächsten Mal wieder.
      }
    }, NACHFRAGE_MS)

    return () => {
      gestoppt = true
      clearInterval(uhr)
    }
  }, [start, zustand, nummer, nachweis, onBezahlt])

  if (nichtVerfuegbar) return null
  if (arten !== null && arten.length === 0) {
    return (
      <section className="bezahlen" aria-label={t('bezahlen.aria')}>
        <h2>{t('bezahlen.titel')}</h2>
        <p className="beschreibung">{t('bezahlen.keineArten')}</p>
      </section>
    )
  }

  const art = arten?.find((eintrag) => eintrag.key === gewaehlt) ?? null

  return (
    <section className="bezahlen" aria-label={t('bezahlen.aria')}>
      <h2>{t('bezahlen.jetzt')}</h2>

      {arten === null && <Ladehinweis text={t('bezahlen.artenLaden')} />}

      {/* Abgelaufen und gescheitert sind zwei verschiedene Sätze: einmal darf
          derselbe Weg noch einmal, einmal soll ein anderer her. */}
      {!start && zustand === 'expired' && (
        <p className="hinweis" role="status">{t('bezahlen.abgelaufen')}</p>
      )}
      {!start && zustand === 'failed' && (
        <p className="hinweis hinweis-fehler" role="alert">
          {t('bezahlen.gescheitert')}
          {nochEinmal ? t('bezahlen.nochEinmal') : t('bezahlen.andereArt')}
        </p>
      )}

      {fehler && (
        <p className="hinweis hinweis-fehler" role="alert">
          {fehler}
        </p>
      )}

      {/* Die Auswahl steht, solange nichts angefordert ist. Danach zeigt der
          Abschnitt, was zu tun ist — und darunter den Weg zurück zur Wahl. */}
      {!start && arten !== null && arten.length > 0 && (
        <>
          {arten.length > 1 && (
            <div className="wahlgruppe" role="radiogroup" aria-label={t('kasse.zahlungsart')}>
              {arten.map((eintrag) => (
                <label className="wahl" key={eintrag.key} data-gewaehlt={gewaehlt === eintrag.key}>
                  <input
                    type="radio"
                    name="zahlungsart"
                    value={eintrag.key}
                    checked={gewaehlt === eintrag.key}
                    onChange={() => setGewaehlt(eintrag.key)}
                  />
                  <span>
                    <strong>{eintrag.label}</strong>
                    {/* Der Hinweis kommt vom Server und wird unverändert
                        gezeigt: „Sie werden zu SumUp geleitet" erspart die
                        Frage, ob die Bestellung schon aufgegeben ist. */}
                    {eintrag.hint && <span className="feld-hinweis">{eintrag.hint}</span>}
                  </span>
                </label>
              ))}
            </div>
          )}

          <button
            className="knopf knopf-gross knopf-breit"
            type="button"
            disabled={startet || !gewaehlt}
            onClick={() => void anfordern(gewaehlt)}
          >
            {startet
              ? t('bezahlen.wirdVorbereitet')
              : art
                ? t('bezahlen.mitArt', { art: art.label })
                : t('bezahlen.artWaehlen')}
          </button>
        </>
      )}

      {start && (
        <>
          <p className="beschreibung">
            {t('bezahlen.zuZahlen')} <strong>{preis(start.amount)}</strong>
            {start.recipient ? <> {t('bezahlen.anEmpfaenger', { empfaenger: start.recipient })}</> : null}.
          </p>

          {start.kind === 'qr' && <AlsCode start={start} preis={preis} />}
          {start.kind === 'redirect' && <AlsWeiterleitung start={start} art={art} />}
          {start.kind === 'manual' && <AlsHinweis start={start} />}

          {start.kind !== 'manual' && (
            <>
              <p className="bezahlen-warten" role="status" aria-live="polite">
                {t('bezahlen.warten')}
              </p>
              <p className="feld-hinweis">{t('bezahlen.seiteSchliessen')}</p>
            </>
          )}

          {/* Der Weg zurück zur Wahl. Bei den manuellen Arten ist er der
              einzige Knopf im Abschnitt — und der einzige, der etwas tut. */}
          {(arten?.length ?? 0) > 1 && (
            <p>
              <button
                className="knopf-verweis"
                type="button"
                onClick={() => {
                  // Die laufende Anforderung einziehen, damit sie niemand
                  // später doch noch bezahlt. Der Server tut das beim Wechsel
                  // ebenfalls — hier für den Fall, dass gar keine zweite Art
                  // gewählt wird.
                  if (start.kind !== 'manual') void cancelPayment(nummer, nachweis)
                  setStart(null)
                  setZustand('none')
                }}
              >
                {t('bezahlen.andereWaehlen')}
              </button>
            </p>
          )}
        </>
      )}
    </section>
  )
}

/**
 * Der QR-Fall: Code und Knopf nebeneinander.
 *
 * Das ist kein Gestaltungsschmuck. Wer am Schreibtisch bestellt, scannt den
 * Code mit dem Telefon. Wer ohnehin am Telefon bestellt, kann keinen Code
 * scannen, den er selbst anzeigt — für ihn ist der Knopf da.
 */
function AlsCode({ start, preis }: { start: PaymentStart; preis: (wert: number) => string }) {
  const t = useT()
  return (
    <>
      {start.qrUrl && (
        <div className="bezahlen-code">
          <img
            src={start.qrUrl}
            alt={t('bezahlen.codeAlt', { betrag: preis(start.amount) })}
            width={240}
            height={240}
          />
        </div>
      )}

      {start.payUrl && (
        <p className="bezahlen-app">
          <a className="knopf" href={start.payUrl} target="_blank" rel="noreferrer noopener">
            {t('bezahlen.aufDiesemGeraet')}
          </a>
        </p>
      )}
    </>
  )
}

/** Der Weiterleitungsfall: ein Knopf, der zum Anbieter führt. */
function AlsWeiterleitung({ start, art }: { start: PaymentStart; art: PaymentMethod | null }) {
  const t = useT()

  if (!start.payUrl) {
    return (
      <p className="hinweis hinweis-fehler" role="alert">
        {t('bezahlen.seiteNichtOffen')}
      </p>
    )
  }

  return (
    <p className="bezahlen-app">
      {/* Im selben Fenster: Der Anbieter schickt den Kunden anschließend
          hierher zurück, und eine Rückkehr in ein Fenster, das er schon
          geschlossen hat, gibt es nicht. */}
      <a className="knopf knopf-gross" href={start.payUrl}>
        {art ? t('bezahlen.weiterZu', { art: art.label }) : t('bezahlen.weiterZurSeite')}
      </a>
    </p>
  )
}

/**
 * Der manuelle Fall: nur der Satz des Ladens.
 *
 * Ausdrücklich kein Knopf. Es gibt nichts zu drücken — der Kunde überweist
 * oder zahlt im Laden, und was dabei zu tun ist, weiß der Laden besser als
 * dieses Ladenfenster: Er hinterlegt den Satz, und der steht hier unverändert.
 */
function AlsHinweis({ start }: { start: PaymentStart }) {
  const t = useT()

  return (
    <>
      {/* Der Satz des Ladens unverändert — er weiß, was zu tun ist. Nur wenn
          er keinen hinterlegt hat, spricht das Ladenfenster. */}
      <p className="hinweis" role="status">{start.note || t('bezahlen.manuellStandard')}</p>
      <p className="feld-hinweis">{t('bezahlen.manuellNachsatz')}</p>
    </>
  )
}
